import { z } from "zod";
import { canonicalSha256 } from "./canonicalize.js";
import { capabilityRegistryDigest, capabilityDomainPackCoverageSchema, registryCapabilityEntrySchema, registryResolutionSchema, } from "./capability-registry.js";
import { protocolIdSchema, protocolVersionSchema, sha256DigestSchema, UNIVERSAL_VERIFICATION_PROTOCOL, } from "./identifiers.js";
export const environmentBindingSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    contractDigest: sha256DigestSchema,
    inputSnapshotDigest: sha256DigestSchema,
    outputSnapshotDigest: sha256DigestSchema,
    environmentScopeMembers: z.array(protocolIdSchema).min(1),
    environmentScopeDigest: sha256DigestSchema,
    executionBoundaryDigest: sha256DigestSchema,
    capabilityInventoryDigest: sha256DigestSchema,
    dependencyLockDigest: sha256DigestSchema,
    featureFlagDigest: sha256DigestSchema,
    databaseMigrationDigest: sha256DigestSchema,
    providerAccountDigest: sha256DigestSchema,
    configurationDigests: z.record(sha256DigestSchema),
    evaluatorVersionDigests: z.record(sha256DigestSchema),
    replayEnvironmentDigest: sha256DigestSchema,
    secretCustodyRefs: z.array(protocolIdSchema),
    redactedSecretDigests: z.array(sha256DigestSchema),
    residualUncertainty: z.array(z.string().trim().min(1)),
    sandbox: z
        .object({
        parentEnvironmentId: protocolIdSchema,
        parentGenerationId: protocolIdSchema,
        sandboxInstanceId: protocolIdSchema,
        launchGenerationId: protocolIdSchema,
        workspaceDescriptorDigest: sha256DigestSchema,
        signedBoundaryEpochDigest: sha256DigestSchema,
        attachmentReceiptDigest: sha256DigestSchema,
        childGrantDigest: sha256DigestSchema,
        sandboxPolicyDigest: sha256DigestSchema,
        revocationEpochDigest: sha256DigestSchema,
        boundaryClaimsDigest: sha256DigestSchema,
        observerPrincipalsDigest: sha256DigestSchema,
        custodyExportDigest: sha256DigestSchema,
        collectorMarker: z.enum(["collector-bound", "collector-absent"]),
    })
        .strict()
        .nullable(),
    recreatedFromBindingDigest: sha256DigestSchema.nullable(),
    recreationEquivalence: z.enum(["same_runtime", "equivalent_ephemeral", "different"]).nullable(),
    knownDeviations: z.array(z.string().trim().min(1)),
    bindingDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    for (const custodyRef of value.secretCustodyRefs) {
        if (custodyRef.includes("secret=") || custodyRef.includes("token=")) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["secretCustodyRefs"],
                message: "secrets must be custody references, not raw values",
            });
        }
    }
    if (value.recreationEquivalence === "equivalent_ephemeral" &&
        value.knownDeviations.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["knownDeviations"],
            message: "ephemeral replacement must record known deviations",
        });
    }
});
export const environmentReceiptReuseAssessmentSchema = z
    .object({
    status: z.enum(["eligible", "ineligible", "history_preserved_runtime_not_continuous"]),
    changedDimensions: z.array(protocolIdSchema),
    reasonCodes: z.array(protocolIdSchema),
})
    .strict();
function digestBinding(input, sha256) {
    return canonicalSha256(input, sha256);
}
export function createRegistryDerivedCapabilityInventory(input) {
    const coverage = capabilityDomainPackCoverageSchema.parse(input.resolutionSummary.coverage);
    const registryEntries = input.resolutionSummary.registryEntries
        .map((entry) => registryCapabilityEntrySchema.parse(entry))
        .sort((left, right) => left.predicate.localeCompare(right.predicate));
    const actualRegistryDigest = capabilityRegistryDigest(registryEntries, input.sha256);
    if (coverage.registryDigest !== actualRegistryDigest) {
        throw new Error(`capability registry digest mismatch: coverage=${coverage.registryDigest} actual=${actualRegistryDigest}`);
    }
    const domains = input.resolutionSummary.domains
        .map((domain) => {
        if (domain.status === "RESOLVED" && domain.resolution === null) {
            throw new Error("resolved registry-derived capability domains require resolution details");
        }
        return {
            domain: domain.domain,
            predicate: domain.predicate,
            status: domain.status,
            resolution: domain.resolution ? registryResolutionSchema.parse(domain.resolution) : null,
            plannedGap: domain.plannedGap,
        };
    })
        .sort((left, right) => {
        const domainOrder = left.domain.localeCompare(right.domain);
        if (domainOrder !== 0)
            return domainOrder;
        return (left.predicate ?? "").localeCompare(right.predicate ?? "");
    });
    const complete = coverage.complete &&
        domains.length > 0 &&
        domains.every((domain) => domain.status === "RESOLVED") &&
        input.resolutionSummary.complete === true;
    return {
        source: "capability-registry-resolution-summary",
        tenantId: input.resolutionSummary.tenantId,
        environmentId: input.resolutionSummary.environmentId,
        registryDigest: coverage.registryDigest,
        coverage,
        registryEntries,
        domains,
        resolvedPredicates: domains
            .filter((domain) => domain.status === "RESOLVED" && domain.predicate)
            .map((domain) => domain.predicate)
            .sort(),
        unresolvedPredicates: domains
            .filter((domain) => domain.status !== "RESOLVED" && domain.predicate)
            .map((domain) => domain.predicate)
            .sort(),
        plannedGaps: domains
            .filter((domain) => domain.plannedGap)
            .map((domain) => domain.plannedGap)
            .sort(),
        complete,
    };
}
export function createEnvironmentBinding(input) {
    const environmentScopeMembers = [...input.environmentScopeMembers].sort();
    const withoutDigest = {
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        contractDigest: input.contractDigest,
        inputSnapshotDigest: input.inputSnapshotDigest,
        outputSnapshotDigest: input.outputSnapshotDigest,
        environmentScopeMembers,
        environmentScopeDigest: canonicalSha256(environmentScopeMembers, input.sha256),
        executionBoundaryDigest: canonicalSha256(input.executionBoundaryParts, input.sha256),
        capabilityInventoryDigest: canonicalSha256(input.capabilityInventory, input.sha256),
        dependencyLockDigest: input.dependencyLockDigest,
        featureFlagDigest: input.featureFlagDigest,
        databaseMigrationDigest: input.databaseMigrationDigest,
        providerAccountDigest: input.providerAccountDigest,
        configurationDigests: Object.fromEntries(Object.entries(input.configurationDigests).sort()),
        evaluatorVersionDigests: Object.fromEntries(Object.entries(input.evaluatorVersionDigests).sort()),
        replayEnvironmentDigest: canonicalSha256(input.replayEnvironment, input.sha256),
        secretCustodyRefs: [...(input.secretCustodyRefs ?? [])].sort(),
        redactedSecretDigests: [...(input.redactedSecretDigests ?? [])].sort(),
        residualUncertainty: [...(input.residualUncertainty ?? [])].sort(),
        sandbox: input.sandbox ?? null,
        recreatedFromBindingDigest: input.recreatedFromBindingDigest ?? null,
        recreationEquivalence: input.recreationEquivalence ?? null,
        knownDeviations: [...(input.knownDeviations ?? [])].sort(),
    };
    return environmentBindingSchema.parse({
        ...withoutDigest,
        bindingDigest: digestBinding(withoutDigest, input.sha256),
    });
}
export function createEnvironmentBindingFromCapabilityRegistry(input) {
    return createEnvironmentBinding({
        ...input,
        capabilityInventory: createRegistryDerivedCapabilityInventory({
            resolutionSummary: input.capabilityResolutionSummary,
            sha256: input.sha256,
        }),
    });
}
export function assessEnvironmentReceiptReuse(input) {
    const original = environmentBindingSchema.parse(input.original);
    const candidate = environmentBindingSchema.parse(input.candidate);
    const dimensions = [
        ["contract", "contractDigest"],
        ["input-snapshot", "inputSnapshotDigest"],
        ["output-snapshot", "outputSnapshotDigest"],
        ["environment-scope", "environmentScopeDigest"],
        ["execution-boundary", "executionBoundaryDigest"],
        ["capability-inventory", "capabilityInventoryDigest"],
        ["dependency-lock", "dependencyLockDigest"],
        ["feature-flags", "featureFlagDigest"],
        ["database-migrations", "databaseMigrationDigest"],
        ["provider-account", "providerAccountDigest"],
        ["replay-environment", "replayEnvironmentDigest"],
    ];
    const changedDimensions = dimensions
        .filter(([, key]) => original[key] !== candidate[key])
        .map(([dimension]) => dimension);
    if (candidate.recreatedFromBindingDigest === original.bindingDigest &&
        candidate.recreationEquivalence === "equivalent_ephemeral") {
        return environmentReceiptReuseAssessmentSchema.parse({
            status: "history_preserved_runtime_not_continuous",
            changedDimensions,
            reasonCodes: ["ephemeral-replacement-history-preserved", "runtime-continuity-not-claimed"],
        });
    }
    if (changedDimensions.length > 0) {
        return environmentReceiptReuseAssessmentSchema.parse({
            status: "ineligible",
            changedDimensions,
            reasonCodes: changedDimensions.map((dimension) => `${dimension}-changed`),
        });
    }
    return environmentReceiptReuseAssessmentSchema.parse({
        status: "eligible",
        changedDimensions: [],
        reasonCodes: ["environment-binding-identical"],
    });
}
